import {
    CompensationProcedureDetailed,
    CompensateRisksData,
    CompensateRisksDataItem,
    ServiceCompensationProcedure,
    ServiceCompensationProcedureDetailed,
    ServiceCompensateRiskData,
} from "@/types/compensation-procedures";
import SodService from "./sod-service";
import { nanoid } from "@reduxjs/toolkit";
import { reportsService } from "./reports-service";

class CompensationProceduresService extends SodService {
    async getCompensationProcedures(params) {
        const config = this.generateConfigForGetEntities(params);

        const data = await this.get("/sod/compensation-procedures/list", config);

        const compensationProcedures = data && data.rows.map((item) => this.parseCompensationProcedure(item));
        const total = data && data.total;

        return { compensationProcedures, total };
    }

    async getCompensationProceduresFilters(params, signal) {
        return this.getFilters("/sod/compensation-procedures/filters", params, signal);
    }

    async getCompensationProceduresColumns() {
        return await this.getColumns("/sod/compensation-procedures/columns");
    }

    async getCompensationProcedureChangeHistory(key: string, params) {
        const config = this.generateConfigForGetEntities(params, null);
        const data = await this.get(`/sod/compensation-procedures/detailed/change-history/${key}`, config);
        return {
            total: data.total,
            logs: data.rows.map((item) => this.parseUserActionLog(item)),
        };
    }

    async getCompensationProcedureDetailed(compensationProcedureId: string) {
        const data = await this.get(`/sod/compensation-procedures/detailed/${encodeURIComponent(compensationProcedureId)}`);

        return this.parseCompensationProcedureDetailed(data);
    }

    async getCompensationProceduresSearchHelp(params, signal) {
        const config = this.generateConfigForGetEntities(params, signal);

        const data = await this.get("/sod/compensation-procedures/list", config);

        const compensationProcedures = data && data.rows.map((item) => this.parseCompensationProcedureSearchHelp(item));
        const total = data && data.total;

        return { rows: compensationProcedures, total };
    }

    async deleteCompensationProcedure(id: string) {
        return this.delete(`/sod/compensation-procedures/detailed/${encodeURIComponent(id)}`);
    }

    async editCompensationProcedure(compensationProcedure: CompensationProcedureDetailed) {
        const config = {
            params: {},
        };
        const data = await this.put(
            `/sod/compensation-procedures/detailed/${encodeURIComponent(compensationProcedure.id)}`,
            this.parseCompensationProcedureToServer(compensationProcedure),
            config
        );

        return this.parseCompensationProcedureDetailed(data);
    }

    async createCompensationProcedure(compensationProcedure: CompensationProcedureDetailed) {
        const config = {
            params: {},
        };
        const data = await this.post(
            `/sod/compensation-procedures/detailed/${encodeURIComponent(compensationProcedure.id)}`,
            this.parseCompensationProcedureToServer(compensationProcedure),
            config
        );

        return this.parseCompensationProcedureDetailed(data);
    }

    async getDataForCompensateRisks(users: string[], risks: { key: string; text: string }[]): Promise<CompensateRisksData> {
        const employeeFilter =
            users && users.length > 0
                ? {
                      field: "employee",
                      sign: "IN",
                      value: users.join(","),
                  }
                : null;

        const riskFilter =
            risks && risks.length > 0
                ? {
                      field: "riskId",
                      sign: "IN",
                      value: risks.join(","),
                  }
                : null;

        const params: {
            filters: { field: string; sign: string; value: string }[];
        } = {
            filters: [],
        };

        if (riskFilter) {
            params.filters.push(riskFilter);
        }

        if (employeeFilter) {
            params.filters.push(employeeFilter);
        }

        const config = this.generateConfigForGetEntities(params);
        config.headers = {
            "Auth-Session": await reportsService.getSessionId(),
        };
        const data = await this.get("/sod/reports/user-level/employee-compensation", config);

        return { risks: data.rows.map((item) => this.parseDataFoCompensateRisks(item)) };
    }

    async getRiskEmployee(params, signal) {
        const config = this.generateConfigForGetEntities(params, signal);
        config.headers = {
            "Auth-Session": await reportsService.getSessionId(),
        };
        const data = await this.get(`/sod/reports/user-level/compensation-filters`, config);

        const rows = data?.rows;
        const total = data?.count;

        return { rows, total };
    }
      
    async getRiskEmployeeDetailed(id) {
        // const data = await this.get(`core/integration/employee-detailed/${id}`);
        return {
            id: id,
        };
        // return data;
    }
    async getCompensationProceduresForCompensateRisks(params, risk_id, signal) {
        const config = this.generateConfigForGetEntities(params, signal);
        config.headers = {
            "Auth-Session": await reportsService.getSessionId(),
        };
        const data = await this.get(`/sod/reports/user-level/compensation-filters${risk_id ? `?risk=${risk_id}` : ""}`, config);

        const rows = data?.rows;
        const total = data?.count;

        return { rows, total };
    }
    async getCompensatedRisks(params, employee_id, signal) {
        const config = this.generateConfigForGetEntities(params, signal);
        config.headers = {
            "Auth-Session": await reportsService.getSessionId(),
        };
        const data = await this.get(`/sod/reports/user-level/compensation-filters${employee_id ? `?employee_id=${employee_id}` : ""}`, config);

        const rows = data?.rows;
        const total = data?.count;

        return { rows, total };
    }

    async createCompensation(form: CompensateRisksData, users: string[], risks: { key: string; text: string }[]) {
        const employeeFilter =
            users && users.length > 0
                ? {
                      field: "employee",
                      sign: "IN",
                      value: users.join(","),
                  }
                : null;

        const riskFilter =
            risks && risks.length > 0
                ? {
                      field: "riskId",
                      sign: "IN",
                      value: risks.map((risk) => risk.key).join(","),
                  }
                : null;

        const params: {
            filters: { field: string; sign: string; value: string }[];
        } = {
            filters: [],
        };

        if (riskFilter) {
            params.filters.push(riskFilter);
        }

        if (employeeFilter) {
            params.filters.push(employeeFilter);
        }

        const config = this.generateConfigForGetEntities(params);

        config.headers = {
            "Auth-Session": await reportsService.getSessionId(),
        };

        const parsedForm = form.risks.filter((risk) => risk.changeStatus).map((risk) => this.parseCompensateRiskDataToServer(risk));

        await this.post("/sod/reports/user-level/employee-compensation", parsedForm, config);
    }

    parseCompensationProcedureSearchHelp(item) {
        return {
            key: item.compensation_procedure_id,
            text: item.description,
        };
    }

    parseCompensationProcedure(item: ServiceCompensationProcedure) {
        return {
            id: item.compensation_procedure_id,
            description: item.description,
        };
    }
    parseCompensationProcedureDetailed(item: ServiceCompensationProcedureDetailed) {
        return {
            id: item.compensation_procedure_id,
            description: item.description,
            risks: item.risks.map((risk) => ({
                riskId: risk.risk_id,
                description: risk.description,
                id: nanoid(),
            })),
            compensatedEmployees: item.compensated_employees.map((employee) => ({
                id: nanoid(),
                riskId: employee.risk_id,
                employeeId: employee.employee,
                procedureId: item.compensation_procedure_id,
                riskDescription: employee.risk_description,
            })),
        };
    }

    parseCompensationProcedureToServer(compensationProcedure: CompensationProcedureDetailed) {
        return {
            compensation_procedure_id: compensationProcedure.id,
            description: compensationProcedure.description,
            risks: compensationProcedure.risks
                .filter((risk) => risk.changeStatus)
                .map((risk) => ({
                    risk_id: risk.riskId,
                    change_status: risk.changeStatus,
                })),
        };
    }

    parseDataFoCompensateRisks(item: ServiceCompensateRiskData): CompensateRisksDataItem {
        return {
            id: nanoid(),
            riskId: item.risk,
            employeeId: item.employee,
            procedureId: item.procedure,
        };
    }

    parseCompensateRiskDataToServer(item: CompensateRisksDataItem) {
        return {
            employee: item.employeeId,
            risk: item.riskId,
            procedure: item.procedureId,
            change_status: item.changeStatus || null,
        };
    }
}

const compensationProceduresService = new CompensationProceduresService();

export { compensationProceduresService };
export default CompensationProceduresService;
